import { defineStore } from 'pinia'

/** 允许同一路由名打开多个标签（按关键 params 区分） */
const MULTI_TAB_ROUTES = {
  'system-dict-data': 'dictType',
  'mp-product-detail': 'id',
  'mp-card-template-edit': 'id',
}

export function resolveViewKey(route) {
  if (!route) return ''
  const name = route.name
  if (!name) return route.fullPath || ''
  const paramKey = MULTI_TAB_ROUTES[name]
  if (paramKey) {
    const val = route.params?.[paramKey]
    if (val != null && String(val) !== '') {
      return `${name}:${val}`
    }
  }
  return String(name)
}

const DASHBOARD_TAB = {
  viewKey: 'dashboard',
  path: '/dashboard',
  fullPath: '/dashboard',
  name: 'dashboard',
  title: '工作台',
  affix: true,
  noCache: false,
}

function shortTitle(raw, max = 12) {
  const t = String(raw || '').trim()
  if (!t) return ''
  return t.length > max ? `${t.slice(0, max)}…` : t
}

function resolveTitle(route) {
  if (route.name === 'system-dict-data') {
    return '字典数据'
  }
  if (route.name === 'mp-product-detail') {
    const short = shortTitle(route.query?.title)
    return short ? `详情·${short}` : '产品详情'
  }
  if (route.name === 'mp-card-template-edit') {
    return String(route.params?.id || '') === 'new' ? '新建名片模板' : '编辑名片模板'
  }
  return route.meta?.title || route.name || '未命名'
}

export { resolveTitle }

export const useTagsViewStore = defineStore('tagsView', {
  state: () => ({
    visitedViews: [{ ...DASHBOARD_TAB }],
    refreshKeys: {},
  }),
  actions: {
    initTags() {
      this.visitedViews = [{ ...DASHBOARD_TAB }]
      this.refreshKeys = {}
    },

    addView(route) {
      if (!route?.name || route.meta?.hidden) return

      const viewKey = resolveViewKey(route)
      const title = resolveTitle(route)
      const existing = this.visitedViews.find((v) => v.viewKey === viewKey)
      if (existing) {
        existing.fullPath = route.fullPath
        existing.path = route.path
        existing.title = title
        return
      }

      this.visitedViews.push({
        viewKey,
        path: route.path,
        fullPath: route.fullPath,
        name: route.name,
        title,
        affix: route.meta?.affix === true || route.name === 'dashboard',
        noCache: route.meta?.noCache === true,
      })
    },

    delView(viewKey) {
      const idx = this.visitedViews.findIndex((v) => v.viewKey === viewKey)
      if (idx < 0) return null

      const view = this.visitedViews[idx]
      if (view.affix) return null

      this.visitedViews.splice(idx, 1)
      delete this.refreshKeys[viewKey]
      return { view, index: idx }
    },

    /** 按打开顺序关闭最早的可关闭标签（跳过 affix，优先保留当前页） */
    closeOldest(excludeViewKey) {
      const target =
        this.visitedViews.find((v) => !v.affix && v.viewKey !== excludeViewKey) ??
        this.visitedViews.find((v) => !v.affix)
      if (!target) return false
      this.delView(target.viewKey)
      return true
    },

    delOthers(viewKey) {
      this.visitedViews = this.visitedViews.filter((v) => v.affix || v.viewKey === viewKey)
      const keep = new Set(this.visitedViews.map((v) => v.viewKey))
      Object.keys(this.refreshKeys).forEach((k) => {
        if (!keep.has(k)) delete this.refreshKeys[k]
      })
    },

    delAll() {
      this.visitedViews = this.visitedViews.filter((v) => v.affix)
      const keep = new Set(this.visitedViews.map((v) => v.viewKey))
      Object.keys(this.refreshKeys).forEach((k) => {
        if (!keep.has(k)) delete this.refreshKeys[k]
      })
    },

    refreshView(viewKey) {
      this.refreshKeys[viewKey] = (this.refreshKeys[viewKey] || 0) + 1
    },

    pageKey(viewKey) {
      const rk = this.refreshKeys[viewKey] || 0
      return `${viewKey}__${rk}`
    },
  },
})
